import { useState, type ReactElement } from "react";
import { useLocation } from "react-router-dom";
import { Modal, Button, BodyText, Stack, Flex } from "@knowtex-ai/components";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { useTutorial } from "../Tutorial/useTutorial";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import { useAnswerLanguagePrompt } from "../../hooks/useLanguagePrompt";
import { useProviderAttestationStatus } from "../../hooks/useProviderAttestation";
import {
  useProviderSettingsQuery,
  useUpdateProviderSettingsMutation,
} from "../../hooks/useProviderSettings";
import { isStandaloneUser as getIsStandaloneUser } from "../../utils/standaloneUser";
import { Events, trackEvent } from "../../lib/analytics";
import { resolveLanguagePromptState } from "./languagePromptVisibility";

// The tutorial context is plain JS; this is the slice of it the prompt reads.
interface TutorialState {
  isActive: boolean;
  isLoading: boolean;
  shouldAutoStart: boolean;
}

const AUTH_ROUTE_PREFIXES = ["/login", "/standalone", "/auth/"];

/**
 * Asks whether the provider conducts sessions in languages other than English.
 * Yes selects the multilingual model, no selects the English model.
 *
 * Shown while `language_model_popup.show_on_login` is true. Answering or
 * dismissing lowers the flag. Leaving the page without doing either asks again
 * next time. Render only for signed-in, subscribed users.
 */
export default function LanguagePrompt(): ReactElement | null {
  const location = useLocation();
  const gate = useFeatureGate("model-type-selector");
  const {
    isActive: isTutorialActive,
    isLoading: isTutorialLoading,
    shouldAutoStart: isTutorialQueued,
  } = useTutorial() as TutorialState;
  const isStandaloneUser = getIsStandaloneUser();
  // Keeps the prompt closed even if lowering the flag fails and the settings
  // cache rolls back. The flag stays raised, so it asks again next session.
  const [isClosed, setIsClosed] = useState(false);

  const isOnAuthRoute = AUTH_ROUTE_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );
  const hasModelTypeFeature = !gate.isLoading && gate.hasFeature();

  // Nothing is fetched for users who could never see the prompt.
  const isEligible = !isStandaloneUser && !isOnAuthRoute && hasModelTypeFeature;

  const answerMutation = useAnswerLanguagePrompt();
  const settingsQuery = useProviderSettingsQuery(undefined, isEligible);
  const { mutate: updateProviderSettings } = useUpdateProviderSettingsMutation();
  // Shares its query key with the dashboard's own gate, so this adds no request
  // there.
  const attestationQuery = useProviderAttestationStatus(isEligible);

  const state = resolveLanguagePromptState({
    isStandaloneUser,
    isOnAuthRoute,
    hasModelTypeFeature,
    isAttestationLoading: attestationQuery.isLoading,
    isAttestationBlocking: Boolean(
      attestationQuery.data?.is_required && !attestationQuery.data?.is_completed
    ),
    isTutorialActive,
    isTutorialPending: isTutorialLoading || isTutorialQueued,
    showOnLoginFlag: settingsQuery.data?.settings?.language_model_popup?.show_on_login,
  });

  // Driven through `open` rather than unmounted so the dialog can animate out.
  const isOpen = !isClosed && state === "show";

  const close = () => {
    setIsClosed(true);
    updateProviderSettings(
      { language_model_popup: { show_on_login: false } },
      {
        onError: (error) => {
          Sentry.captureException(error, {
            extra: { context: "LanguagePrompt lowering show_on_login" },
          });
        },
      }
    );
  };

  const handleAnswer = async (conductsNonEnglishSessions: boolean) => {
    try {
      const result = await answerMutation.mutateAsync(conductsNonEnglishSessions);
      trackEvent(Events.LANGUAGE_PROMPT_ANSWERED, {
        conducts_non_english_sessions: conductsNonEnglishSessions,
        model_type: result.modelType,
      });
      close();
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: "LanguagePrompt answer" },
      });
      // Stays open so the answer can be retried.
      toast.error("Failed to save your answer. Please try again.");
    }
  };

  const handleDismiss = () => {
    trackEvent(Events.LANGUAGE_PROMPT_DISMISSED);
    close();
  };

  const isSubmitting = answerMutation.isPending;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(nextOpen) => {
        // Closing mid-save would count as a dismissal and hide a failed save's retry.
        if (!nextOpen && !isSubmitting) handleDismiss();
      }}
      // Keeps the question from being lost to a stray click.
      closeOnPressOutside={false}
      size="small"
      title="Do you conduct sessions in languages other than English?"
    >
      <Stack gap={20}>
        <BodyText muted>
          This tells Knowtex which speech model to use for your notes. If you see
          patients in more than one language — including switching between
          languages in the same visit — choose Yes. You can change this any time
          in Settings.
        </BodyText>

        <Flex justifyContent="flex-end" gap={12}>
          <Button
            variant="secondary"
            onClick={() => handleAnswer(true)}
            disabled={isSubmitting}
            loading={isSubmitting && answerMutation.variables === true}
          >
            Yes
          </Button>
          <Button
            variant="primary"
            onClick={() => handleAnswer(false)}
            disabled={isSubmitting}
            loading={isSubmitting && answerMutation.variables === false}
          >
            No, English only
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
}
